import { useCallback, useLayoutEffect, useState } from 'react';

import { getScrollBufferPx } from './scrollOffsets';

export const FLOATING_DOCK_CLEARANCE_INLINE = '--floating-dock-clearance-inline';
export const FLOATING_DOCK_CLEARANCE_BLOCK = '--floating-dock-clearance-block';

/**
 * How far the floating dock reaches into the window from its right and bottom
 * edges, plus a gap: the room the page leaves it.
 *
 * The dock has no width of its own (its children hang off its right edge), so
 * its children are measured. A hidden child, the Save bar while a unit is open
 * on a phone, measures as an empty box at the origin, and is skipped.
 */
export function measureFloatingDockClearance(
  dock: HTMLElement,
  viewport: { height: number; width: number },
  gap: number,
) {
  const boxes = Array.from(dock.children)
    .map((child) => child.getBoundingClientRect())
    .filter((box) => box.width > 0 && box.height > 0);

  if (boxes.length === 0) {
    return null;
  }

  const left = Math.min(...boxes.map((box) => box.left));
  const top = Math.min(...boxes.map((box) => box.top));

  return {
    block: Math.ceil(Math.max(0, viewport.height - top) + gap),
    inline: Math.ceil(Math.max(0, viewport.width - left) + gap),
  };
}

/**
 * Publishes the floating dock's clearance on the document as two CSS
 * variables, as the app shell publishes `--header-height`: the chapter page
 * keeps its reading column, its end and its scrolled-to controls clear of the
 * dock with them.
 *
 * The clearance is the widest the dock has reached in the current `mode`, and
 * stays while the dock shows nothing. The reading column takes its width from
 * it: when it followed the dock's live width, the whole chapter re-wrapped
 * each time the Save bar's status changed, a unit opened, or the assistant
 * took the dock's place. A new mode starts over.
 *
 * Returns the ref callback for the dock.
 */
export function useFloatingDockClearance(mode: string) {
  const [dock, setDock] = useState<HTMLElement | null>(null);

  // Before the page is painted, so the reading column is laid out once.
  useLayoutEffect(() => {
    if (!dock || typeof document === 'undefined') {
      return undefined;
    }

    const rootStyle = document.documentElement.style;
    let published: { block: number; inline: number } | null = null;
    const update = () => {
      const clearance = measureFloatingDockClearance(
        dock,
        {
          height: document.documentElement.clientHeight,
          width: document.documentElement.clientWidth,
        },
        getScrollBufferPx(),
      );

      if (!clearance) {
        return;
      }

      const next = published
        ? {
            block: Math.max(published.block, clearance.block),
            inline: Math.max(published.inline, clearance.inline),
          }
        : clearance;

      if (published && next.block === published.block && next.inline === published.inline) {
        return;
      }

      published = next;
      rootStyle.setProperty(FLOATING_DOCK_CLEARANCE_INLINE, `${next.inline}px`);
      rootStyle.setProperty(FLOATING_DOCK_CLEARANCE_BLOCK, `${next.block}px`);
    };

    update();
    // The dock's offset from the window's edges changes at a breakpoint
    // without any of its children changing size.
    window.addEventListener('resize', update);

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    const observeChildren = () => {
      resizeObserver?.disconnect();
      Array.from(dock.children).forEach((child) => {
        resizeObserver?.observe(child);
      });
    };

    observeChildren();

    // The Save bar and the floating actions come and go with edit mode. A
    // change inside either changes its size, which the resize observer sees.
    const mutationObserver =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver(() => {
            observeChildren();
            update();
          });

    mutationObserver?.observe(dock, { childList: true });

    return () => {
      window.removeEventListener('resize', update);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      rootStyle.removeProperty(FLOATING_DOCK_CLEARANCE_INLINE);
      rootStyle.removeProperty(FLOATING_DOCK_CLEARANCE_BLOCK);
    };
  }, [dock, mode]);

  return useCallback((element: HTMLElement | null) => {
    setDock(element);
  }, []);
}
